/**
 * 管理后台：会员信息 / 组织小组 / 轨迹导出 / 批量图标
 */
import { useState } from 'react';
import { Modal, Form, Input } from 'antd';
import { message } from '../utils/antdMessage';
import { confirmDangerousAction } from '../pages/admin/dangerConfirm';
import siteConfig from '../config/site-config.js';

export default function useAdminMembers({ apiRequest, token, handleLogout }) {
  const API_BASE_URL = siteConfig.getApiUrl();

  const [members, setMembers] = useState([]);
  const [memberPagination, setMemberPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [memberSearch, setMemberSearch] = useState('');
  const [memberGroupFilter, setMemberGroupFilter] = useState('');
  const [memberOrgFilter, setMemberOrgFilter] = useState('');
  const [organizations, setOrganizations] = useState({});
  const [orgInterconnect, setOrgInterconnect] = useState({});
  const [selectedMemberKeys, setSelectedMemberKeys] = useState([]);
  const [memberMsg, setMemberMsg] = useState('');
  const [memberModalVisible, setMemberModalVisible] = useState(false);
  const [memberEditModalVisible, setMemberEditModalVisible] = useState(false);
  const [memberForm] = Form.useForm();
  const [memberEditForm] = Form.useForm();

  const [orgModal, setOrgModal] = useState({ visible: false, type: '', org: '', group: '', newName: '' });
  const [editMemberModal, setEditMemberModal] = useState({ visible: false, record: null, name: '', org: '', group: '' });
  const [exportModal, setExportModal] = useState({ visible: false, ids: [], start: '', end: '', format: 'kml' });
  const [batchIconModal, setBatchIconModal] = useState({ visible: false, icon: 'water_drop' });

  const fetchMembers = async (page = 1, overrides = {}) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: memberPagination.pageSize.toString(),
        search: overrides.search !== undefined ? overrides.search : memberSearch,
        group_name: overrides.group_name !== undefined ? overrides.group_name : memberGroupFilter,
        org_name: overrides.org_name !== undefined ? overrides.org_name : memberOrgFilter
      });

      const data = await apiRequest(`/api/admin/members?${params}`);
      if (data.success) {
        setMembers(data.members);
        setMemberPagination(prev => ({
          ...prev,
          current: data.pagination.current,
          total: data.pagination.total
        }));
      } else {
        setMemberMsg(data.error || '获取失败');
      }
    } catch (e) {
      setMemberMsg('网络错误');
    }
  };

  const fetchOrganizations = async () => {
    try {
      const data = await apiRequest('/api/admin/organizations');
      if (data.success) {
        setOrganizations(data.organizations);
        setOrgInterconnect(data.interconnect || {});
      }
    } catch (e) {
      console.error('获取组织列表失败:', e);
    }
  };

  const handleDeleteMemberLocation = async (device_unique_id, start_time, end_time, delete_all = false, confirmText = null) => {
    let confirm_text = confirmText;
    if (!confirm_text) {
      try {
        confirm_text = await confirmDangerousAction({
          title: '删除位置记录',
          content: '确定要删除这些位置记录吗？此操作不可恢复！'
        });
      } catch {
        return;
      }
    }

    try {
      const data = await apiRequest('/api/admin/members/location-history', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_unique_id, start_time, end_time, delete_all, confirm_text })
      });

      if (data.success) {
        message.success(data.message);
        fetchMembers(memberPagination.current);
      } else {
        setMemberMsg(data.error || '删除失败');
      }
    } catch (e) {
      setMemberMsg('网络错误');
    }
  };

  const handleDeleteMember = async (device_unique_id, confirmText = null) => {
    let confirm_text = confirmText;
    if (!confirm_text) {
      try {
        confirm_text = await confirmDangerousAction({
          title: '删除设备队员',
          content: '确定要删除这个会员及其所有位置记录吗？此操作不可恢复！'
        });
      } catch {
        return;
      }
    }

    try {
      const data = await apiRequest('/api/admin/members/device', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_unique_id, confirm_text })
      });

      if (data.success) {
        message.success(data.message);
        fetchMembers(memberPagination.current);
      } else {
        setMemberMsg(data.error || '删除失败');
      }
    } catch (e) {
      setMemberMsg('网络错误');
    }
  };

  const handleUpdateMember = async (values) => {
    try {
      const data = await apiRequest('/api/admin/members/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values)
      });
      
      if (data.success) {
        message.success(data.message);
        setMemberEditModalVisible(false);
        memberEditForm.resetFields();
        fetchMembers(memberPagination.current);
      } else {
        message.error(data.error || '更新失败');
      }
    } catch (e) {
      message.error('网络错误');
    }
  };

  const handleSaveMember = async (values) => {
    const data = await apiRequest('/api/admin/members/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values)
    });
    if (data.success) {
      message.success('保存成功');
      setMemberModalVisible(false);
      memberForm.resetFields();
      fetchMembers(memberPagination.current);
    } else {
      message.error(data.error || '保存失败');
    }
  };
  const handleToggleOrgInterconnect = async (orgName, enabled) => {
    const prev = !!orgInterconnect[orgName];
    setOrgInterconnect((current) => ({ ...current, [orgName]: enabled }));
    const data = await apiRequest('/api/admin/organizations/interconnect', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ org_name: orgName, enabled })
    });
    if (data.success) {
      message.success(enabled ? '已开通联' : '已关闭通联');
    } else {
      setOrgInterconnect((current) => ({ ...current, [orgName]: prev }));
      message.error(data.error || '保存通联开关失败');
    }
  };
  const handleAddOrg = async () => {
    let newName = '';
    Modal.confirm({
      title: '新增组织',
      content: (
        <Input placeholder="请输入组织名称" onChange={e => { newName = e.target.value; }} />
      ),
      onOk: async () => {
        if (!newName.trim()) return message.error('请输入组织名称');
        const data = await apiRequest('/api/admin/organizations/add', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ org_name: newName.trim() })
        });
        if (data.success) { message.success('添加成功'); fetchOrganizations(); } else { message.error(data.error || '添加失败'); }
      }
    });
  };
  // 编辑组织
  const handleRenameOrg = (org) => {
    let newName = org;
    Modal.confirm({
      title: '重命名组织',
      content: (
        <Input defaultValue={org} onChange={e => { newName = e.target.value; }} />
      ),
      onOk: async () => {
        if (!newName.trim()) return message.error('请输入新名称');
        const data = await apiRequest('/api/admin/organizations/rename', {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ old_org_name: org, new_org_name: newName.trim() })
        });
        if (data.success) { message.success('重命名成功'); fetchOrganizations(); fetchMembers(); } else { message.error(data.error || '重命名失败'); }
      }
    });
  };
  // 删除组织
  const handleDeleteOrgModal = (org) => {
    Modal.confirm({
      title: '删除组织',
      content: `确定要删除组织"${org}"吗？该组织下所有设备的组织字段将被清空。`,
      onOk: async () => {
        const data = await apiRequest('/api/admin/organizations/delete', {
          method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ org_name: org })
        });
        if (data.success) { message.success('删除成功'); fetchOrganizations(); fetchMembers(); } else { message.error(data.error || '删除失败'); }
      }
    });
  };
  // 新增小组
  const handleAddGroupOrg = (org) => {
    let newName = '';
    Modal.confirm({
      title: `为组织"${org}"新增小组`,
      content: (
        <Input placeholder="请输入小组名称" onChange={e => { newName = e.target.value; }} />
      ),
      onOk: async () => {
        if (!newName.trim()) return message.error('请输入小组名称');
        const data = await apiRequest('/api/admin/groups/add', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ org_name: org, group_name: newName.trim() })
        });
        if (data.success) { message.success('添加成功'); fetchOrganizations(); fetchMembers(); } else { message.error(data.error || '添加失败'); }
      }
    });
  };
  // 编辑小组
  const handleRenameGroupOrg = (org, group) => {
    let newName = group;
    Modal.confirm({
      title: `重命名小组（组织：${org}）`,
      content: (
        <Input defaultValue={group} onChange={e => { newName = e.target.value; }} />
      ),
      onOk: async () => {
        if (!newName.trim()) return message.error('请输入新名称');
        const data = await apiRequest('/api/admin/groups/rename', {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ org_name: org, old_group_name: group, new_group_name: newName.trim() })
        });
        if (data.success) { message.success('重命名成功'); fetchOrganizations(); fetchMembers(); } else { message.error(data.error || '重命名失败'); }
      }
    });
  };
  // 删除小组
  const handleDeleteGroupOrg = (org, group) => {
    Modal.confirm({
      title: `删除小组（组织：${org}）`,
      content: `确定要删除小组"${group}"吗？该小组下所有设备的小组字段将被清空。`,
      onOk: async () => {
        const data = await apiRequest('/api/admin/groups/delete', {
          method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ org_name: org, group_name: group })
        });
        if (data.success) { message.success('删除成功'); fetchOrganizations(); fetchMembers(); } else { message.error(data.error || '删除失败'); }
      }
    });
  };

  // 编辑会员弹窗提交
  const handleEditMember = async (record) => {
    console.log('编辑会员，传入数据:', record);
    setMemberEditModalVisible(true);
    console.log('设置表单值:', {
      device_unique_id: record.device_unique_id,
      name: record.name,
      group_name: record.group_name,
      org_name: record.org_name
    });
    memberEditForm.setFieldsValue(record);
    console.log('表单值设置完成');
  };

  // 会员信息管理表格批量操作
  const handleBatchDeleteLocation = async () => {
    if (selectedMemberKeys.length === 0) return message.warning('请先选择会员');
    let confirm_text;
    try {
      confirm_text = await confirmDangerousAction({
        title: '批量删除位置信息',
        content: `确定要删除选中 ${selectedMemberKeys.length} 个会员的所有位置记录吗？此操作不可恢复！`
      });
    } catch {
      return;
    }
    // delete_all=true 且带 device_unique_id：仅清空该设备位置（后端已按设备过滤）
    for (const device_unique_id of selectedMemberKeys) {
      await handleDeleteMemberLocation(device_unique_id, null, null, true, confirm_text);
    }
    setSelectedMemberKeys([]);
    fetchMembers(memberPagination.current);
  };

  const handleBatchDeleteMember = async () => {
    if (selectedMemberKeys.length === 0) return message.warning('请先选择会员');
    let confirm_text;
    try {
      confirm_text = await confirmDangerousAction({
        title: '批量删除会员',
        content: `确定要删除选中 ${selectedMemberKeys.length} 个会员及其所有位置记录吗？此操作不可恢复！`
      });
    } catch {
      return;
    }
    for (const device_unique_id of selectedMemberKeys) {
      await handleDeleteMember(device_unique_id, confirm_text);
    }
    setSelectedMemberKeys([]);
    fetchMembers(memberPagination.current);
  };

  // 导出轨迹（走统一 API 基址 + 鉴权下载）
  const handleExportTrack = async () => {
    if (!exportModal.ids.length) return message.warning('请选择要导出的会员');
    const params = new URLSearchParams();
    params.append('device_unique_ids', exportModal.ids.join(','));
    if (exportModal.start) params.append('start', exportModal.start);
    if (exportModal.end) params.append('end', exportModal.end);
    params.append('format', exportModal.format || 'kml');
    const baseWithoutApi = API_BASE_URL.replace(/\/api\/?$/, '');
    try {
      const res = await fetch(`${baseWithoutApi}/api/history/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) {
        handleLogout();
        message.error('登录已过期，请重新登录');
        return;
      }
      if (!res.ok) {
        const errText = await res.text();
        message.error(errText || '导出失败');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `track_export.${exportModal.format || 'kml'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportModal({ visible: false, ids: [], start: '', end: '', format: 'kml' });
      message.success('导出成功');
    } catch (e) {
      message.error('导出失败：网络错误');
    }
  };

  const openBatchIconModal = () => {
    if (selectedMemberKeys.length === 0) {
      message.warning('请先选择要设置图标的会员');
      return;
    }
    setBatchIconModal({ visible: true, icon: batchIconModal.icon || 'water_drop' });
  };

  // 批量设置图标（弹窗确认后提交）
  const submitBatchSetIcon = async () => {
    if (selectedMemberKeys.length === 0) {
      message.warning('请先选择要设置图标的会员');
      return;
    }
    const baseWithoutApi = API_BASE_URL.replace(/\/api\/?$/, '');
    try {
      const response = await fetch(`${baseWithoutApi}/api/device/batch-icon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          device_unique_ids: selectedMemberKeys,
          icon: batchIconModal.icon || 'water_drop'
        })
      });
      if (response.status === 401) {
        handleLogout();
        message.error('登录已过期，请重新登录');
        return;
      }
      const data = await response.json();
      if (data.success) {
        message.success(data.message);
        setBatchIconModal({ visible: false, icon: 'water_drop' });
        setSelectedMemberKeys([]);
        fetchMembers(memberPagination.current);
      } else {
        message.error(data.error || '批量设置图标失败');
      }
    } catch (error) {
      console.error('批量设置图标失败:', error);
      message.error('网络错误，请重试');
    }
  };

  return {
    members, memberPagination, setMemberPagination, memberSearch, setMemberSearch, memberGroupFilter, setMemberGroupFilter,
    memberOrgFilter, setMemberOrgFilter, organizations, orgInterconnect, handleToggleOrgInterconnect, selectedMemberKeys, setSelectedMemberKeys, memberMsg,
    memberModalVisible, setMemberModalVisible, memberEditModalVisible, setMemberEditModalVisible,
    memberForm, memberEditForm, fetchMembers, fetchOrganizations, handleEditMember, handleDeleteMember, handleBatchDeleteMember,
    handleBatchDeleteLocation, handleExportTrack, openBatchIconModal, submitBatchSetIcon, handleSaveMember, handleUpdateMember,
    exportModal, setExportModal, batchIconModal, setBatchIconModal, orgModal, setOrgModal, editMemberModal, setEditMemberModal,
    handleAddOrg, handleRenameOrg, handleDeleteOrgModal, handleAddGroupOrg, handleRenameGroupOrg, handleDeleteGroupOrg
  };
}
